import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LOADER_COLORS } from './loading/types';

/** Key to mark that we should show update animation on next load */
export const UPDATE_ANIMATION_KEY = 'spirit_show_update_animation';

// Animation text phases
const TEXT_PHASES = [
  { text: 'Gathering new blessings...', hebrew: 'אוספים ברכות חדשות' },
  { text: 'Polishing the experience...', hebrew: 'משפרים את החוויה' },
  { text: 'Almost ready...', hebrew: 'כמעט מוכן' },
];

// Failsafe timeout - max 30 seconds before forcing completion
const FAILSAFE_TIMEOUT_MS = 30000;

interface UpdateAnimationProps {
  /** Whether the app is ready to display */
  isReady: boolean;
  /** Called when the animation is complete and should be removed */
  onComplete: () => void;
  /** Minimum animation duration in milliseconds (default: 6000) */
  minDuration?: number;
}

/**
 * Full-screen update animation overlay.
 * Shows while the app loads after an update refresh.
 * Waits for both minimum duration AND app ready before fading out.
 *
 * Includes failsafe to prevent stuck loading screen.
 */
export function UpdateAnimation({ isReady, onComplete, minDuration = 6000 }: UpdateAnimationProps) {
  const [textIndex, setTextIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const hasCompletedRef = useRef(false);
  const failsafeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable completion handler that prevents double-calling
  const handleComplete = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    // Clear failsafe timeout
    if (failsafeTimeoutRef.current) {
      clearTimeout(failsafeTimeoutRef.current);
      failsafeTimeoutRef.current = null;
    }

    onComplete();
  }, [onComplete]);

  // Fade in on mount and hide HTML preloader
  useEffect(() => {
    requestAnimationFrame(() => setFadeIn(true));

    // Hide the HTML preloader
    const htmlPreloader = document.getElementById('spirit-preloader');
    if (htmlPreloader) {
      htmlPreloader.style.opacity = '0';
      htmlPreloader.style.display = 'none';
    }

    // Set up failsafe timeout
    failsafeTimeoutRef.current = setTimeout(() => {
      if (!hasCompletedRef.current) {
        console.warn(
          '[UpdateAnimation] Failsafe timeout triggered - forcing completion after',
          FAILSAFE_TIMEOUT_MS,
          'ms'
        );
        setFadeOut(true);
        setTimeout(() => handleComplete(), 500);
      }
    }, FAILSAFE_TIMEOUT_MS);

    return () => {
      if (failsafeTimeoutRef.current) {
        clearTimeout(failsafeTimeoutRef.current);
        failsafeTimeoutRef.current = null;
      }
    };
  }, [handleComplete]);

  // Progress animation
  useEffect(() => {
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const newProgress = Math.min(elapsed / minDuration, 1);

      // Update text phase based on progress
      if (newProgress < 0.33) {
        setTextIndex(0);
      } else if (newProgress < 0.66) {
        setTextIndex(1);
      } else {
        setTextIndex(2);
      }

      if (newProgress < 1) {
        requestAnimationFrame(animate);
      } else {
        setMinTimeElapsed(true);
      }
    };

    requestAnimationFrame(animate);
  }, [minDuration]);

  // Fade out when both ready and min time elapsed
  useEffect(() => {
    if (isReady && minTimeElapsed && !fadeOut && !hasCompletedRef.current) {
      setFadeOut(true);
      setTimeout(() => handleComplete(), 500); // Match fade duration
    }
  }, [isReady, minTimeElapsed, fadeOut, handleComplete]);

  const currentPhase = TEXT_PHASES[textIndex];
  const opacityWhenNotFadingOut = fadeIn ? 1 : 0;

  const overlay = (
    <div
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center"
      style={{
        background: `linear-gradient(135deg, ${LOADER_COLORS.primaryWhisper} 0%, ${LOADER_COLORS.primaryGhost} 50%, ${LOADER_COLORS.primaryFaint} 100%)`,
        opacity: fadeOut ? 0 : opacityWhenNotFadingOut,
        transition: fadeOut ? 'opacity 500ms ease-out' : 'opacity 300ms ease-out',
      }}
    >
      {/* Floating particles background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <Particle key={i} index={i} />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6">
        {/* Animated Torah scroll icon */}
        <div className="relative mb-8">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${LOADER_COLORS.primaryPale} 0%, ${LOADER_COLORS.primaryLight} 100%)`,
              boxShadow: `0 0 40px ${LOADER_COLORS.primaryPale}`,
              animation: 'pulse 2s ease-in-out infinite',
            }}
          >
            <TorahScrollIcon />
          </div>
          {/* Sparkle effects around the icon */}
          <Sparkles />
        </div>

        {/* Hebrew text */}
        <p
          className="text-2xl mb-2 transition-opacity duration-500"
          style={{
            fontFamily: '"Times New Roman", "David", "Narkisim", serif',
            color: LOADER_COLORS.primaryDark,
            opacity: fadeIn ? 1 : 0,
          }}
        >
          {currentPhase.hebrew}
        </p>

        {/* English text */}
        <p
          className="text-lg mb-8 transition-opacity duration-500"
          style={{
            fontFamily: 'Georgia, serif',
            color: LOADER_COLORS.primary,
            opacity: fadeIn ? 1 : 0,
          }}
        >
          {currentPhase.text}
        </p>

        {/* Progress bar */}
        <div
          className="w-64 h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: LOADER_COLORS.primaryPale }}
        >
          <div
            className="h-full rounded-full"
            style={{
              background: `linear-gradient(90deg, ${LOADER_COLORS.primary} 0%, ${LOADER_COLORS.primaryLight} 100%)`,
              animation: `progressFill ${minDuration}ms linear forwards`,
            }}
          />
        </div>

        {/* Subtitle */}
        <p className="mt-4 text-sm" style={{ color: LOADER_COLORS.primaryDark, opacity: 0.7 }}>
          Renewing the Spirit of Shabbat
        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 40px ${LOADER_COLORS.primaryPale};
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 60px ${LOADER_COLORS.primaryLight};
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-20px) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes sparkle {
          0%, 100% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            transform: scale(1) rotate(180deg);
            opacity: 1;
          }
        }

        @keyframes progressFill {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );

  return createPortal(overlay, document.body);
}

/** Floating particle component */
function Particle({ index }: { index: number }) {
  const delay = (index * 0.3) % 4;
  const duration = 4 + (index % 3);
  const left = (index * 17) % 100;
  const size = 4 + (index % 4) * 2;

  const colors = [
    LOADER_COLORS.gold,
    LOADER_COLORS.goldBright,
    LOADER_COLORS.primaryLight,
    LOADER_COLORS.primary,
  ];
  const color = colors[index % colors.length];

  return (
    <div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        left: `${left}%`,
        bottom: -20,
        backgroundColor: color,
        animation: `float ${duration}s ease-in-out ${delay}s infinite`,
        boxShadow: `0 0 ${size * 2}px ${color}`,
      }}
    />
  );
}

/** Sparkle effects around the main icon */
function Sparkles() {
  const sparklePositions = [
    { top: -10, left: -10, delay: 0 },
    { top: -5, right: -15, delay: 0.3 },
    { bottom: -8, left: -12, delay: 0.6 },
    { bottom: -5, right: -10, delay: 0.9 },
    { top: '50%', left: -20, delay: 1.2 },
    { top: '50%', right: -18, delay: 1.5 },
  ];

  return (
    <>
      {sparklePositions.map((pos, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            ...pos,
            animation: `sparkle 2s ease-in-out ${pos.delay}s infinite`,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill={LOADER_COLORS.gold}>
            <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
          </svg>
        </div>
      ))}
    </>
  );
}

/** Simplified Torah scroll icon */
function TorahScrollIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left scroll handle */}
      <rect x="6" y="8" width="6" height="32" rx="3" fill={LOADER_COLORS.primary} />
      <rect x="7" y="6" width="4" height="4" rx="2" fill={LOADER_COLORS.primaryDark} />
      <rect x="7" y="38" width="4" height="4" rx="2" fill={LOADER_COLORS.primaryDark} />

      {/* Right scroll handle */}
      <rect x="36" y="8" width="6" height="32" rx="3" fill={LOADER_COLORS.primary} />
      <rect x="37" y="6" width="4" height="4" rx="2" fill={LOADER_COLORS.primaryDark} />
      <rect x="37" y="38" width="4" height="4" rx="2" fill={LOADER_COLORS.primaryDark} />

      {/* Parchment */}
      <rect x="12" y="10" width="24" height="28" rx="2" fill={LOADER_COLORS.parchment} />
      <rect
        x="12"
        y="10"
        width="24"
        height="28"
        rx="2"
        stroke={LOADER_COLORS.gold}
        strokeWidth="1"
      />

      {/* Text lines */}
      <rect x="16" y="16" width="16" height="2" rx="1" fill={LOADER_COLORS.primaryPale} />
      <rect x="16" y="22" width="16" height="2" rx="1" fill={LOADER_COLORS.primaryPale} />
      <rect x="16" y="28" width="12" height="2" rx="1" fill={LOADER_COLORS.primaryPale} />
    </svg>
  );
}

/**
 * Trigger an update refresh - sets flag and reloads immediately.
 * The update animation will show on the new page load.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function triggerUpdateRefresh(): void {
  sessionStorage.setItem(UPDATE_ANIMATION_KEY, 'true');
  window.location.reload();
}

// Module-level cache to handle React StrictMode double-mounting
let animationChecked = false;
let animationShouldShow = false;

/**
 * Check if we should show the update animation on this page load.
 * Result is cached at module level to handle StrictMode.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function shouldShowUpdateAnimation(): boolean {
  // Only check once per page load
  if (animationChecked) {
    return animationShouldShow;
  }

  animationChecked = true;
  const show = sessionStorage.getItem(UPDATE_ANIMATION_KEY);

  if (show) {
    sessionStorage.removeItem(UPDATE_ANIMATION_KEY);
    animationShouldShow = true;
    return true;
  }

  animationShouldShow = false;
  return false;
}
